import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';

export const CALL_URL = 'https://calendar.app.google/so8xSSLuNyzph2vEA';
export const CONTACT_EMAIL = 'info@qireagency.com';
const UPDATED = '8 October 2026';

const DOCS = {
  privacy: ['Privacy Policy', [
    ['Who we are', `This website, qireagency.com, is run by Qire Agency (Amon Cheruiyot), Kenya. Contact: ${CONTACT_EMAIL}.`],
    ['What we collect', 'What you enter in our forms (such as name, email address, phone number, project details, budget and timeline), the link you came from if it carries a source tag, and basic technical logs kept by our hosting provider, such as IP address and browser type. If you are a client, we also keep project and billing records and the account details you use for the client portal.'],
    ['Analytics and fonts', 'We use Plausible Analytics, which does not use cookies and does not track you across sites. Our pages load fonts from Google Fonts and the country flags in the phone field from flagcdn.com, which may receive your IP address.'],
    ['How we use it', 'To reply to your enquiry by email or phone, prepare quotes, schedule calls, deliver and invoice projects, and keep a record in our client system. We do not sell your data or use it for unrelated marketing.'],
    ['Who else sees it', 'Service providers that help us run the business (hosting, email, scheduling, payment and customer-record tools), and authorities where the law requires it.'],
    ['How long we keep it', 'As long as needed to deal with your enquiry or project and meet legal and accounting duties. Enquiries that lead to no work are deleted when no longer useful, or sooner on request.'],
    ['Your rights', `You can ask to see, correct or delete your data, or object to its use, by emailing ${CONTACT_EMAIL}. We handle personal data in line with applicable law, including Kenya's Data Protection Act, 2019.`],
    ['Other', 'Our services are not meant for anyone under 18. If we update this policy, we change the date above.'],
  ]],
  terms: ['Terms of Service', [
    ['About this website', 'This website describes software development services offered by Qire Agency. It is an invitation to enquire, not a binding offer. A project begins only when you accept a written quote or agreement.'],
    ['Quotes and payment', 'Each project has a price and delivery date in its written quote. Unless the quote says otherwise, work starts after you approve the specification and pay a 50% deposit, and the balance is due on final delivery.'],
    ['Your responsibilities', 'You give accurate information, and confirm you have the right to share your strategy or material and have it built. You are responsible for testing any trading software on a demo account before using real funds, and for your own trading decisions.'],
    ['Ownership and confidentiality', 'Ownership of delivered work, and confidentiality of your material, are set out in the written agreement for each project. We keep ownership of our pre-existing tools and libraries.'],
    ['Acceptable use', 'We do not build tools to deceive others, mislead investors, manipulate markets or break the law.'],
    ['Liability', 'To the extent the law allows, we are not liable for trading losses or indirect loss, and our total liability for a project is limited to the fees you paid for it.'],
    ['Law and changes', 'These terms are governed by the laws of Kenya. If we update them, we change the date above.'],
  ]],
  disclaimer: ['Risk Disclaimer', [
    ['Not financial advice', 'We are a software development agency. Nothing on this website is investment, financial, tax or legal advice, and we do not recommend any strategy, instrument or broker.'],
    ['Trading risk', 'Trading leveraged products such as forex and CFDs carries a high risk of loss and is not suitable for everyone. Only trade with money you can afford to lose.'],
    ['No guarantee of results', 'Trading software automates the rules you give it. It does not guarantee profit. Backtests use historical data and do not predict future results. Live results can differ because of spreads, slippage, execution speed, broker conditions and connection problems.'],
    ['Examples', 'Code, rules and results shown on this website are illustrative and are not a trading recommendation.'],
    ['Trademarks', 'MetaTrader and MQL5 are trademarks of MetaQuotes Ltd. Qire Agency is not affiliated with or endorsed by MetaQuotes or any broker.'],
  ]],
};

// ── SHARED THEME (one saved choice across landing page, EA page and legal pages) ──
const THEME_KEY = 'qire-theme';
const readTheme = () => { try { return localStorage.getItem(THEME_KEY) || 'dark'; } catch (e) { return 'dark'; } };
export function useTheme() {
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    const sync = () => setTheme(readTheme());
    window.addEventListener('qire-theme', sync);
    return () => window.removeEventListener('qire-theme', sync);
  }, []);
  useEffect(() => { document.documentElement.setAttribute('data-theme', theme); }, [theme]);
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(THEME_KEY, next); } catch (e) { /* storage unavailable */ }
    setTheme(next);
    window.dispatchEvent(new Event('qire-theme'));
  };
  return [theme, toggle];
}

const STYLES = `
.sh{position:fixed;top:0;left:0;right:0;z-index:9999;isolation:isolate;background:var(--black);border-bottom:1px solid var(--border);font-family:var(--font-body)}
.sh-in{max-width:1100px;margin:0 auto;padding:0 1.5rem;height:64px;display:flex;align-items:center;justify-content:space-between;gap:1rem}
.sh-logo{background:none;border:0;padding:0;flex-shrink:0;min-width:0}
.sh-links{display:flex;align-items:center;gap:1.75rem;position:relative}
.sh-link{background:none;border:0;cursor:pointer;padding:0 0 4px;font-family:inherit;font-size:.96rem;letter-spacing:.02em;color:var(--white-dim);transition:color .2s;white-space:nowrap}
.sh-link:hover,.sh-link.on{color:var(--white)}
.sh-ind{position:absolute;bottom:0;height:1.5px;background:var(--white);transition:left .3s ease,width .3s ease,opacity .2s ease}
.sh-right{display:flex;align-items:center;gap:.75rem}
.sh-book{display:inline-flex;align-items:center;gap:.45rem;flex-shrink:0;background:var(--teal);color:var(--on-teal);text-decoration:none;padding:.55rem 1.1rem;border-radius:2px;font-family:inherit;font-size:.9rem;font-weight:500;letter-spacing:.03em;border:1px solid var(--teal);cursor:pointer;box-shadow:0 0 22px -6px rgba(var(--glow),.55)}
.sh-book:hover{filter:brightness(1.08)}
.sh-portal{display:inline-flex;align-items:center;gap:.45rem;flex-shrink:0;background:transparent;color:var(--white);padding:.55rem 1.1rem;border-radius:2px;border:1px solid var(--border-bright);font-family:inherit;font-size:.9rem;font-weight:500;letter-spacing:.03em;cursor:pointer;transition:border-color .2s}
.sh-portal:hover{border-color:var(--white)}
.sh-sq{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:36px;height:36px;border-radius:3px;border:1px solid var(--border);background:var(--surface);color:var(--white);cursor:pointer;font-size:.95rem}
.sh-sq:hover,.sh-sq.open{border-color:var(--white)}
.sh-ham{display:none}
.sh-ov{position:fixed;inset:0;z-index:40;background:rgba(0,0,0,.5)}
.sh-menu{overflow:hidden;max-height:0;opacity:0;transition:max-height .32s cubic-bezier(.4,0,.2,1),opacity .25s;background:var(--black)}
.sh-menu.open{max-height:520px;opacity:1;border-top:1px solid var(--border);box-shadow:0 16px 48px rgba(0,0,0,.18)}
.sh-menu-in{padding:.5rem 1rem 1rem}
.sh-mlbl{font-size:.78rem;letter-spacing:.22em;text-transform:uppercase;color:var(--white-dim);padding:.75rem .5rem .4rem}
.sh-mi{width:100%;display:flex;align-items:center;justify-content:space-between;padding:.75rem .5rem;background:transparent;border:0;border-left:2px solid transparent;cursor:pointer;color:var(--white);font-family:inherit;font-size:.98rem;letter-spacing:.02em;transition:all .15s;margin-bottom:1px;text-align:left}
.sh-mi.on{border-left-color:var(--white);padding-left:.75rem}
.sh-mi i{width:5px;height:5px;border-radius:50%;background:var(--white)}
.sh-mportal{margin-top:.6rem;width:100%;display:flex;align-items:center;justify-content:center;background:transparent;color:var(--white);padding:.8rem 1rem;border-radius:2px;border:1px solid var(--border-bright);cursor:pointer;font-family:inherit;font-size:.96rem;font-weight:500;letter-spacing:.03em}
.sh-mbook{margin-top:.9rem;width:100%;display:flex;align-items:center;justify-content:center;gap:.5rem;background:var(--teal);color:var(--on-teal);text-decoration:none;padding:.8rem 1rem;border-radius:2px;border:0;cursor:pointer;font-family:inherit;font-size:.96rem;font-weight:500;letter-spacing:.03em}
@media(max-width:768px){.sh-links,.sh-book,.sh-portal{display:none}.sh-ham{display:flex}.sh-in{padding:0 1rem}}
.sf{--f-text:var(--white);--f-muted:var(--white-dim);--f-border:var(--border);background:var(--surface);border-top:1px solid var(--border);color:var(--f-text);font-family:var(--font-body)}
[data-theme="light"] .sf{--f-text:#fff;--f-muted:rgba(255,255,255,.6);--f-border:rgba(255,255,255,.14);background:#022d27;border-top:0}
.sf-in{max-width:1100px;margin:0 auto;padding:4rem 1.5rem 2.25rem}
.sf-grid{display:grid;grid-template-columns:1.3fr 1fr 1fr 1fr;gap:3rem;margin-bottom:3rem}
.sf-brand{display:flex;align-items:center;gap:.65rem;margin-bottom:1.1rem;background:none;border:0;padding:0;cursor:pointer}
.sf-mark{width:30px;height:30px;border-radius:4px;display:flex;align-items:center;justify-content:center;background:var(--teal);color:var(--on-teal);font:800 .8rem var(--font-display)}
[data-theme="light"] .sf-mark{background:#fff;color:#022d27}
.sf-name{font:700 1rem var(--font-display);color:var(--f-text);letter-spacing:.02em}
.sf-about{font-size:.94rem;line-height:1.75;color:var(--f-muted);max-width:280px;margin:0}
.sf-title{font-size:.82rem;letter-spacing:.2em;text-transform:uppercase;color:var(--f-text);margin-bottom:1.1rem}
.sf-col{display:flex;flex-direction:column;align-items:flex-start;gap:.7rem}
.sf-col a,.sf-col button,.sf-mail{background:none;border:0;padding:0;cursor:pointer;font:inherit;font-size:.94rem;color:var(--f-muted);text-decoration:none;text-align:left;transition:color .2s}
.sf-col a:hover,.sf-col button:hover,.sf-mail:hover{color:var(--f-text)}
.sf-mail{display:block;margin-bottom:1.1rem}
.sf-soc{display:flex;flex-wrap:wrap;gap:.5rem}
.sf-soc a{width:30px;height:30px;border:1px solid var(--f-border);border-radius:3px;display:flex;align-items:center;justify-content:center;color:var(--f-muted);transition:border-color .2s,color .2s}
.sf-soc a:hover{border-color:var(--f-text);color:var(--f-text)}
.sf-risk{margin:0 0 1.75rem;padding:1rem 1.25rem;border:1px solid var(--f-border);border-radius:var(--radius);font-size:.8rem;line-height:1.65;color:var(--f-muted)}
.sf-risk strong{color:var(--f-text)}
.sf-bottom{border-top:1px solid var(--f-border);padding-top:1.5rem;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:1rem}
.sf-copy{font-size:.84rem;letter-spacing:.05em;color:var(--f-muted)}
.sf-bl{display:flex;align-items:center;gap:1.25rem}
.sf-bl a{font-size:.86rem;letter-spacing:.04em;color:var(--f-muted);text-decoration:none;transition:color .2s}.sf-bl a:hover{color:var(--f-text)}
@media(max-width:960px){.sf-grid{grid-template-columns:1fr 1fr}}
@media(max-width:768px){.sf-grid{grid-template-columns:1fr;gap:2.25rem}}
.fc{position:fixed;right:1.25rem;bottom:1.25rem;z-index:180;display:flex;align-items:center;gap:.45rem;opacity:0;transform:translateY(18px);pointer-events:none;transition:opacity .25s,transform .25s;padding-bottom:env(safe-area-inset-bottom,0)}
.fc.on{opacity:1;transform:none;pointer-events:auto}
.fc-btn{display:inline-flex;align-items:center;gap:.5rem;background:var(--teal);color:var(--on-teal);font:700 .92rem var(--font-body);padding:.8rem 1.35rem;border-radius:999px;border:0;cursor:pointer;text-decoration:none;white-space:nowrap;box-shadow:0 0 0 0 rgba(var(--glow),.55),0 8px 28px rgba(var(--glow),.35);animation:fcGlow 2.6s ease-out infinite}
.fc-btn:hover{filter:brightness(1.08)}
.fc-x{width:28px;height:28px;border-radius:50%;border:1px solid var(--border-bright);background:var(--nav-bg);color:var(--white-dim);cursor:pointer;font-size:1rem;line-height:1}
@keyframes fcGlow{0%{box-shadow:0 0 0 0 rgba(var(--glow),.55),0 8px 28px rgba(var(--glow),.35)}70%{box-shadow:0 0 0 16px rgba(var(--glow),0),0 8px 28px rgba(var(--glow),.35)}100%{box-shadow:0 0 0 0 rgba(var(--glow),0),0 8px 28px rgba(var(--glow),.35)}}
@media(prefers-reduced-motion:reduce){.fc-btn{animation:none}.fc{transition:none}}
@media(max-width:600px){.fc{left:1rem;right:1rem;bottom:1rem;justify-content:center}.fc-btn{flex:1;justify-content:center;padding:.95rem 1.2rem}}
.sh-promo{display:flex;align-items:center;justify-content:space-between;gap:1.5rem;flex-wrap:wrap;border:1px solid var(--teal-dim);background:var(--teal-glow);border-radius:var(--radius-lg);padding:1.4rem 1.6rem;box-shadow:0 0 44px -14px rgba(var(--glow),.45)}
.sh-promo strong{display:block;font-family:var(--font-display);font-size:1.1rem;color:var(--white);margin-bottom:.25rem}
.sh-promo p{margin:0;color:var(--white-dim);font-size:.9rem;max-width:34rem}
.sh-promo .tag{display:inline-block;font:600 .68rem var(--font-mono);letter-spacing:.14em;text-transform:uppercase;color:var(--teal);margin-bottom:.4rem}
@media(max-width:600px){
  .sh-in{height:58px;padding:0 .9rem;gap:.6rem}
  .sh-sq{width:34px;height:34px}
  .sh-menu-in{padding:.35rem .85rem .85rem}
  .sh-mi{font-size:.92rem;padding:.7rem .45rem}
  .sh-mportal,.sh-mbook{font-size:.9rem;padding:.75rem .9rem}
  .sf-in{padding:2.5rem 1rem 1.4rem}
  .sf-grid{gap:1.45rem;margin-bottom:1.6rem}
  .sf-brand{margin-bottom:.75rem}
  .sf-about{font-size:.88rem;line-height:1.65}
  .sf-title{font-size:.74rem;letter-spacing:.16em;margin-bottom:.65rem}
  .sf-col{gap:.55rem}
  .sf-col a,.sf-col button,.sf-mail{font-size:.88rem}
  .sf-mail{overflow-wrap:anywhere;margin-bottom:.75rem}
  .sf-risk{font-size:.74rem;line-height:1.6;padding:.8rem .9rem;margin-bottom:1.2rem}
  .sf-bottom{padding-top:1rem;gap:.75rem;align-items:flex-start}
  .sf-copy{font-size:.72rem;line-height:1.5;letter-spacing:.02em}
  .sf-bl{gap:.9rem;flex-wrap:wrap}
  .sf-bl a{font-size:.78rem}
  .sh-promo{padding:1rem;gap:.9rem}
  .sh-promo strong{font-size:1rem}
  .sh-promo p{font-size:.86rem;line-height:1.6}
}
@media(max-width:380px){.sf-in{padding-left:.85rem;padding-right:.85rem}.sh-in{padding-left:.8rem;padding-right:.8rem}}

`;

// ── HEADER: same arrangement as the portfolio (sliding underline, slide-down menu with Book a Call) ──
export function SiteHeader({ current, onSection, cta }) {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const [ind, setInd] = useState({ left: 0, width: 0, opacity: 0 });
  const navRef = useRef(null);
  const linkRefs = useRef({});

  const section = (id) => { setOpen(false); if (onSection) onSection(id); else navigate('/', { state: { scrollTo: id } }); };
  const goTo = (path) => { setOpen(false); navigate(path); };
  const links = [
    ['services', 'Services', () => section('capabilities')],
    ['automation', 'Strategy Automation', () => goTo('/strategy-automation')],
  ];
  const c = cta || { label: 'Book a Call', onClick: () => section('contact') };

  // sliding underline under the active link
  useEffect(() => {
    const update = () => {
      const el = linkRefs.current[current];
      setInd(el ? { left: el.offsetLeft, width: el.offsetWidth, opacity: 1 } : (i) => ({ ...i, opacity: 0 }));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [current]);

  // close on outside tap or scroll, lock page scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpen(false); };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    window.addEventListener('scroll', close);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      window.removeEventListener('scroll', close);
      document.body.style.overflow = '';
    };
  }, [open]);

  const book = (cls, extra) => (c.href
    ? <a className={cls} href={c.href} target="_blank" rel="noopener noreferrer" onClick={extra}>{c.label}</a>
    : <button className={cls} onClick={() => { setOpen(false); c.onClick(); }}>{c.label}</button>);

  return (
    <>
      <style>{STYLES}</style>
      {open && <div className="sh-ov" onClick={() => setOpen(false)} />}
      <nav className="sh" ref={navRef} aria-label="Main">
        <div className="sh-in">
          <button className="q-logo sh-logo" aria-label="Qire Agency home" onClick={() => (onSection ? onSection('hero') : goTo('/'))}>
            <div className="q-logo-mark">Q</div>
            <span className="q-logo-text">Qire<span>Agency</span></span>
          </button>

          <div className="sh-links">
            {links.map(([k, l, fn]) => (
              <button key={k} ref={(el) => { linkRefs.current[k] = el; }} className={`sh-link${current === k ? ' on' : ''}`} onClick={fn}>{l}</button>
            ))}
            <div className="sh-ind" style={{ left: ind.left, width: ind.width, opacity: ind.opacity }} />
          </div>

          <div className="sh-right">
            {book('sh-book')}
            <button className={`sh-sq sh-ham${open ? ' open' : ''}`} aria-label="Menu" aria-expanded={open} onClick={() => setOpen(!open)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">{open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}</svg>
            </button>
          </div>
        </div>

        <div className={`sh-menu${open ? ' open' : ''}`} aria-hidden={!open}>
          <div className="sh-menu-in">
            <div className="sh-mlbl">Navigation</div>
            {links.map(([k, l, fn]) => (
              <button key={k} tabIndex={open ? 0 : -1} className={`sh-mi${current === k ? ' on' : ''}`} onClick={fn}><span>{l}</span>{current === k && <i />}</button>
            ))}
            {book('sh-mbook', () => setOpen(false))}
          </div>
        </div>
      </nav>
    </>
  );
}
export const SiteNav = SiteHeader;

// ── FLOATING CTA: appears after scrolling, hides over the listed sections, glows teal ──
export function FloatingCta({ label, href, onClick, hideOn = [], showAfter = 480 }) {
  const [shown, setShown] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [closed, setClosed] = useState(false);
  const key = hideOn.join(',');

  useEffect(() => {
    const on = () => setShown(window.scrollY > showAfter);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, [showAfter]);

  useEffect(() => {
    const els = hideOn.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return undefined;
    const seen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? seen.add(e.target.id) : seen.delete(e.target.id)));
      setHidden(seen.size > 0);
    }, { threshold: 0.15 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const on = shown && !hidden && !closed;
  return (
    <div className={`fc${on ? ' on' : ''}`} aria-hidden={!on}>
      {href
        ? <a className="fc-btn" href={href} target="_blank" rel="noopener noreferrer" tabIndex={on ? 0 : -1} onClick={onClick}>{label} →</a>
        : <button className="fc-btn" tabIndex={on ? 0 : -1} onClick={onClick}>{label} →</button>}
      <button className="fc-x" aria-label="Dismiss" tabIndex={on ? 0 : -1} onClick={() => setClosed(true)}>×</button>
    </div>
  );
}

const Ico = ({ fill, children }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
// Same seven icons, grouping and order as the portfolio footer. X, Instagram and LinkedIn are the agency accounts.
const SOCIALS = [
  ['GitHub', 'https://github.com/AmonCheruiyotHQ', <Ico><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" /><path d="M9 18c-4.51 2-5-2-7-2" /></Ico>],
  ['Email', `mailto:${CONTACT_EMAIL}`, <Ico><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></Ico>],
  ['X', 'https://x.com/QireAgency', <Ico fill><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-2.32 17.52h1.833L7.084 4.126H5.117z" /></Ico>],
  ['Instagram', 'https://instagram.com/qireagency', <Ico><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></Ico>],
  ['LinkedIn', 'https://linkedin.com/company/qireagency', <Ico><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect width="4" height="12" x="2" y="9" /><circle cx="4" cy="4" r="2" /></Ico>],
  ['Reddit', 'https://reddit.com/user/amoncheruiyothq', <Ico fill><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.248 1.248.688 0 1.25-.561 1.25-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" /></Ico>],
  ['Discord', 'https://discord.gg/amoncheruiyothq', <Ico fill><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.876 19.876 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.955 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.946 2.418-2.157 2.418z" /></Ico>],
];

export function SiteFooter({ onSection }) {
  const navigate = useNavigate();
  const section = (id) => { if (onSection) onSection(id); else navigate('/', { state: { scrollTo: id } }); };
  return (
    <footer className="sf" id="site-footer">
      <style>{STYLES}</style>
      <div className="sf-in">
        <div className="sf-grid">
          <div>
            <button className="sf-brand" onClick={() => (onSection ? onSection('hero') : navigate('/'))} aria-label="Qire Agency home">
              <div className="sf-mark">Q</div><span className="sf-name">Qire Agency</span>
            </button>
            <p className="sf-about">Trading bots, indicators, UIs and HFT systems for traders and trading businesses.</p>
          </div>

          <div>
            <div className="sf-title">Quick Links</div>
            <div className="sf-col">
              <button onClick={() => section('capabilities')}>Services</button>
              <button onClick={() => navigate('/strategy-automation')}>Strategy Automation</button>
              <button onClick={() => section('contact')}>Book a Call</button>
            </div>
          </div>

          <div>
            <div className="sf-title">Contact</div>
            <a className="sf-mail" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <div className="sf-soc">
              {SOCIALS.map(([label, href, icon]) => (
                <a key={label} href={href} target={href.startsWith('mailto') ? undefined : '_blank'} rel="noopener noreferrer" aria-label={label}>{icon}</a>
              ))}
            </div>
          </div>

          <div>
            <div className="sf-title">Legal</div>
            <div className="sf-col">
              <button onClick={() => navigate('/privacy')}>Privacy Policy</button>
              <button onClick={() => navigate('/terms')}>Terms of Service</button>
              <button onClick={() => navigate('/disclaimer')}>Risk Disclaimer</button>
            </div>
          </div>
        </div>

        <p className="sf-risk"><strong>Risk warning:</strong> trading forex and CFDs carries a high risk of loss and is not suitable for everyone. Trading software does not guarantee profit, and backtested or past performance does not predict future results.</p>

        <div className="sf-bottom">
          <span className="sf-copy">© {new Date().getFullYear()} Qire Agency. All rights reserved.</span>
          <div className="sf-bl">
            <a href="https://www.upwork.com/freelancers/~0100bba04ce79cde7a" target="_blank" rel="noopener noreferrer">Upwork</a>
            <a href="https://www.freelancer.com/u/amoncheruiyothq" target="_blank" rel="noopener noreferrer">Freelancer.com</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LegalPage() {
  const navigate = useNavigate();
  const key = useLocation().pathname.replace(/\//g, '').toLowerCase();
  const doc = DOCS[key];

  const navType = useNavigationType();
  useTheme();

  useEffect(() => {
    if (navType !== 'POP') window.scrollTo(0, 0);
    if (doc) document.title = `${doc[0]} | Qire Agency`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  if (!doc) return null;
  return (
    <div style={{ background: 'var(--black)', minHeight: '100vh' }}>
      <SiteHeader />
      <section className="q-section" style={{ paddingTop: '8rem' }}>
        <div className="q-inner" style={{ maxWidth: 780 }}>
          <div className="q-eyebrow">Legal</div>
          <h1 className="q-section-title">{doc[0]}</h1>
          <p style={{ color: 'var(--white-dim)', fontSize: '.85rem', marginBottom: '1rem' }}>Last updated: {UPDATED}</p>
          {doc[1].map(([h, t]) => (
            <div key={h}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', color: 'var(--white)', margin: '1.6rem 0 .4rem' }}>{h}</h3>
              <p style={{ color: 'var(--white-dim)', fontSize: '.94rem', lineHeight: 1.75, margin: 0 }}>{t}</p>
            </div>
          ))}
          <button className="q-btn-secondary" style={{ marginTop: '2.5rem' }} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}>Go back</button>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}